import type { PaymentVerdictAction, PaymentVerdictVm } from '@krp/sale-ui'

import type { CommandFeedback } from '../../../shared/model/feedback'

import type { AqsiSale, AqsiSaleOperation, ResolveDraft } from './types'

/**
 * Сверка операции чека по двум галочкам (решение владельца 04.11.2026, RP-CASH-RECOVERY §4.2, К-128): вместо итога «исполнена / не
 * исполнена» и свободного текста человек подтверждает или меняет то, что знает система, а основное действие выбирают галочки:
 * - деньги есть, чека в ФН нет → «Пробить чек» (`receipt/recover`: служба сама решает, как безопасно отправить чек тем же слипом);
 * - деньги есть, чек в ФН есть → «чек пробит»: `receipt/recover` с `action: 'close-confirmed'` — служба сама ищет чек в AQSI и закрывает
 *   продажу найденной записью; номер чека человек вводит, только если служба ответила 409 `receipt-number-required` (К-130), тогда
 *   записывается сверка операции чека «исполнена» с этим номером;
 * - денег нет (только когда оплата системой не доказана) → записать сверку операции оплаты «не исполнена».
 * Слова записи составляет система — обязательных «причина» и «что проверено» нет.
 */

/** Причина сверки, когда человек отметил обе галочки. */
export const VERDICT_RECEIPT_REASON = 'Сверка по галочкам: деньги списаны, чек пробит в ФН'
/** Причина сверки оплаты, когда человек снял «Деньги списаны». */
export const VERDICT_NO_MONEY_REASON = 'Сверка по галочкам: деньги не списаны'

/** «Что проверено» для записи пробитого чека: номер — со слов человека. */
export const verdictReceiptChecked = (receiptNumber: string): string => `подтвердил на экране, чек №${receiptNumber.trim()}`
/** Что уходит службе с галочкой «чек пробит» без номера: слово человека для журнала. */
export const VERDICT_CLOSE_CONFIRMED_CHECKED = 'подтвердил на экране: деньги списаны, чек пробит в ФН'
/** Отказ `receipt/recover`: в AQSI чека этой продажи нет — службе нужен номер чека из ФН (К-130). */
export const RECEIPT_NUMBER_REQUIRED_CODE = 'receipt-number-required'

/** Служба ответила «укажите номер чека»: итог последней команды `sale.recover` — отказ с этим кодом (не пропадает сам, пока нет новой команды). */
export function receiptNumberRequiredBy(feedback: CommandFeedback | null | undefined): boolean {
  return feedback?.command === 'sale.recover' && feedback.phase === 'rejected' && feedback.error?.code === RECEIPT_NUMBER_REQUIRED_CODE
}

/** «Что проверено» для отметки «денег нет». */
export const VERDICT_NO_MONEY_CHECKED = 'подтвердил на экране: деньги на терминале не списаны'
/** Что уходит службе с «Пробить чек»: слово человека для журнала. */
export const VERDICT_RECOVER_CHECKED = 'подтвердил на экране: деньги списаны, чек в ФН не пробит'

const ACTION_TEXT: Readonly<Record<PaymentVerdictAction, string>> = {
  recover: 'Повторно пробить в фискальном накопителе',
  'record-receipt': 'Записать: чек пробит',
  'payment-not-taken': 'Деньги не списаны',
}

const ACTION_HINT: Readonly<Record<PaymentVerdictAction, string>> = {
  recover: 'Служба проверит кассу и отправит чек этой продажи тем же слипом; второй оплаты не будет.',
  'record-receipt': 'Служба найдёт чек этой продажи в AQSI и закроет продажу; если чека там нет, она попросит номер из ФН.',
  'payment-not-taken': 'Сверка запишет, что оплата не прошла: денег нет, чек не нужен.',
}

/** Оплата доказана системой: картой — служба подтвердила списание, наличными — терминал не участвует. */
export function moneyProven(sale: AqsiSale): boolean {
  return sale.payment === 'Succeeded' || sale.payment === 'NotRequired'
}

function moneyEvidenceOf(sale: AqsiSale): string {
  if (sale.payment === 'NotRequired') return 'Оплата наличными: терминал не участвует, деньги принял кассир.'
  if (sale.payment === 'Succeeded') return `Списание подтверждено службой${sale.slipId === null ? '' : ` · слип ${sale.slipId}`}.`
  return 'Служба не знает, списаны ли деньги: проверьте терминал и отметьте сами.'
}

function fiscalEvidenceOf(sale: AqsiSale, status: AqsiSaleOperation): string | null {
  const said = status.providerMessage?.trim() ?? ''
  const aqsi = said === '' ? '' : ` Касса ответила: «${said}».`
  switch (sale.fiscal) {
    case 'Succeeded':
    case 'IssuedOutside':
      return `Служба знает чек${sale.receiptId === null ? '' : ` №${sale.receiptId}`}.${aqsi}`
    case 'Failed':
      return `Касса чек не пробила.${aqsi}`
    default:
      return `Служба не знает, пробит ли чек.${aqsi}`
  }
}

export interface ReceiptVerdictInput {
  sale: AqsiSale | null
  status: AqsiSaleOperation | undefined
  draft: ResolveDraft
  /** Операция ждёт ручной сверки и прочитана (есть версия). */
  allowed: boolean
  /** Касса продажи известна (`deviceId`): без неё «Пробить чек» адресовать некуда. */
  seatKnown: boolean
  /**
   * Предзаполнение службы из «Проверки в AQSI» (К-129, `prefill`): что она прочитала в AQSI о деньгах и чеке. Человек не трогал галочку —
   * она стоит так; не передано или `null` — по продаже, как прежде.
   */
  prefill?: { moneyTaken: boolean | null; fiscalized: boolean | null }
  /** Служба ответила 409 `receipt-number-required` на «чек пробит»: поле номера открыто, без него действие недоступно. */
  numberRequired?: boolean
}

/** Галочки для операции чека; другая операция (оплата, возврат) — `null`: у неё прежняя форма. */
export function receiptVerdictOf({ sale, status, draft, allowed, seatKnown, prefill, numberRequired = false }: ReceiptVerdictInput): PaymentVerdictVm | null {
  if (status === undefined || status.kind !== 'receipt') return null
  const proven = sale !== null && moneyProven(sale)
  const moneyTaken = proven || (draft.moneyTaken ?? prefill?.moneyTaken) === true
  const fiscalKnown = sale !== null && (sale.fiscal === 'Succeeded' || sale.fiscal === 'IssuedOutside')
  const fiscalized = moneyTaken && (draft.fiscalized ?? prefill?.fiscalized ?? fiscalKnown)
  const action: PaymentVerdictAction = !moneyTaken ? 'payment-not-taken' : fiscalized ? 'record-receipt' : 'recover'
  const blockedReason = verdictBlockedReason({ sale, draft, allowed, seatKnown, action, numberRequired })
  return {
    moneyTaken,
    moneyEditable: sale !== null && !proven,
    moneyEvidence: sale === null ? null : moneyEvidenceOf(sale),
    fiscalized,
    fiscalEvidence: sale === null ? null : fiscalEvidenceOf(sale, status),
    receiptNumber: draft.resultId,
    receiptNumberRequired: action === 'record-receipt' && numberRequired,
    action,
    actionText: ACTION_TEXT[action],
    actionHint: ACTION_HINT[action],
    canAct: blockedReason === null,
    blockedReason,
    busy: draft.phase === 'sending',
    outcomeUnknown: draft.phase === 'unknown',
  }
}

function verdictBlockedReason(input: {
  sale: AqsiSale | null
  draft: ResolveDraft
  allowed: boolean
  seatKnown: boolean
  action: PaymentVerdictAction
  numberRequired: boolean
}): string | null {
  const { sale, draft, allowed, seatKnown, action, numberRequired } = input
  if (draft.phase === 'sending') return 'Отправляется…'
  if (draft.phase === 'unknown') return 'Ответ не получен — нажмите «Проверить статус»; повторять до проверки нельзя.'
  if (draft.phase === 'done') return 'Готово: продажа перечитана.'
  if (!allowed) return 'Читаем операцию у службы — действие станет доступно после чтения.'
  if (sale === null) return 'Читаем продажу у службы — галочки станут доступны после чтения.'
  if (sale.isFinished) return 'Продажа уже завершена — сверять нечего.'
  switch (action) {
    case 'recover':
      return seatKnown ? null : 'Служба не назвала кассу этой продажи — откройте сверку из окна оплаты на кассе.'
    case 'record-receipt':
      // Без номера «чек пробит» уходит службе (`close-confirmed`): ей нужна касса, чтобы адресовать команду; номер — только по её просьбе.
      if (numberRequired) return draft.resultId.trim() === '' ? 'Укажите номер чека из ФН.' : null
      return seatKnown ? null : 'Служба не назвала кассу этой продажи — откройте сверку из окна оплаты на кассе.'
    case 'payment-not-taken':
      return sale.purchaseOperationId === null ? 'У продажи нет операции оплаты — отметить «деньги не списаны» нечем.' : null
  }
}
